import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import "./anniv2026.css";
import "./reveal2026.css";
import "./leaderboard2026.css";
import {Login2026} from "./Login2026";
import {errorMessage, post} from "./api2026";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ExploreIcon from "@mui/icons-material/Explore";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import CasinoIcon from "@mui/icons-material/Casino";

const REFRESH = 5 * 60 * 1000;
// the first ones stay in place, the others scroll
const FIXED_ROWS = 3;
// scroll speed: seconds per row
const SECONDS_PER_ROW = 2.5;

// each board has its own icon and accent color (Nostra palette)
const BOARDS = [
    {id: "players", title: "Les joueurs", description: "Parties jouées", icon: SportsEsportsIcon, accent: "cyan", value: row => row.parties},
    {id: "explorers", title: "Les explorateurs", description: "Jeux différents", icon: ExploreIcon, accent: "periwinkle", value: row => row.games},
    {id: "social", title: "Les sociaux", description: "Joueurs rencontrés", icon: Diversity3Icon, accent: "violet", value: row => row.partners},
];

const GAMES_BOARD = {id: "games", title: "Les jeux", description: "Parties par jeu", icon: CasinoIcon, accent: "gold", value: row => row.parties};

const MIN_TEXT_SCALE = 0.45;

// text on one line, shrunk until it fits the width of its box (cut with an ellipsis below MIN_TEXT_SCALE)
const FitText = ({text}) => {

    const ref = useRef(undefined);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        function fit() {
            const element = ref.current;
            if (!element) {
                return;
            }
            element.style.fontSize = "1em";
            let current = 1;
            for (let i = 0; i < 4 && element.scrollWidth > element.clientWidth + 1; i++) {
                current = Math.max(MIN_TEXT_SCALE, current * element.clientWidth / element.scrollWidth * 0.98);
                element.style.fontSize = current + "em";
            }
            setScale(current);
        }
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, [text]);

    return <span ref={ref} className="leaderboard-fit-text" style={{fontSize: scale + "em"}}>{text}</span>;
}

/*
 * Rows below the fixed ones: if they don't fit, they scroll slowly in a loop
 * (the list is doubled and moved by half its height so the loop is seamless).
 */
const ScrollingRows = ({rows, renderRow, columns = 1}) => {

    const viewportRef = useRef(undefined);
    const contentRef = useRef(undefined);
    const [scrolling, setScrolling] = useState(false);

    useLayoutEffect(() => {
        function check() {
            if (!viewportRef.current || !contentRef.current) {
                return;
            }
            const height = contentRef.current.scrollHeight / (scrolling ? 2 : 1);
            setScrolling(height > viewportRef.current.clientHeight);
        }
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, [rows, scrolling]);

    // with several columns, the list is padded to full lines so that the copy starts on a new line
    const padding = scrolling && rows.length % columns ? columns - rows.length % columns : 0;
    const fillers = [...Array(padding).keys()].map(i => <div key={"filler-" + i}/>);
    return (
        <div className={"leaderboard-scroll" + (scrolling ? " scrolling" : "")} ref={viewportRef}>
            <div ref={contentRef} className={"leaderboard-scroll-content columns-" + columns}
                 style={scrolling ? {animationDuration: (Math.ceil(rows.length / columns) * SECONDS_PER_ROW) + "s"} : {}}>
                {rows.map((row, index) => renderRow(row, index, ""))}
                {fillers}
                {scrolling ? rows.map((row, index) => renderRow(row, index, "copy-")) : undefined}
                {scrolling ? fillers.map(filler => React.cloneElement(filler, {key: "copy-" + filler.key})) : undefined}
            </div>
        </div>
    );
}

const Board = ({board, rows, renderContent}) => {

    function renderRow(row, index, keyPrefix) {
        const rank = keyPrefix === "fixed-" ? index : index + FIXED_ROWS;
        return (
            <div key={keyPrefix + (row.id || row.name)} className={"leaderboard-row rank" + rank}>
                <div className="leaderboard-rank">{rank + 1}</div>
                {renderContent(row)}
                <div className="leaderboard-value">{board.value(row)}</div>
            </div>
        );
    }

    const Icon = board.icon;
    return (
        <div className={"leaderboard-board accent-" + board.accent}>
            <div className="leaderboard-board-header">
                <div className="leaderboard-board-icon"><Icon/></div>
                <div>
                    <div className="leaderboard-board-title">{board.title}</div>
                    <div className="leaderboard-board-description">{board.description}</div>
                </div>
            </div>
            {rows.slice(0, FIXED_ROWS).map((row, index) => renderRow(row, index, "fixed-"))}
            {rows.length > FIXED_ROWS ? <ScrollingRows rows={rows.slice(FIXED_ROWS)} renderRow={renderRow}/> : undefined}
            {rows.length === 0 ? <div className="leaderboard-empty">Pas encore de partie</div> : undefined}
        </div>
    );
}

/*
 * A player category on one line: header and podium on the left,
 * the following ones on the right in three columns, without scrolling.
 */
const PlayerBoard = ({board, rows}) => {

    function renderRow(row, index, keyPrefix) {
        return (
            <div key={keyPrefix + row.name} className="leaderboard-row">
                <div className="leaderboard-rank">{index + FIXED_ROWS + 1}</div>
                <div className="leaderboard-name">{row.name}</div>
                <div className="leaderboard-value">{board.value(row)}</div>
            </div>
        );
    }

    const Icon = board.icon;
    return (
        <div className={"leaderboard-board wide accent-" + board.accent}>
            <div className="leaderboard-wide-left">
                <div className="leaderboard-board-header">
                    <div className="leaderboard-board-icon"><Icon/></div>
                    <div>
                        <div className="leaderboard-board-title">{board.title}</div>
                        <div className="leaderboard-board-description">{board.description}</div>
                    </div>
                </div>
                <div className="leaderboard-podium">
                    {
                        // displayed order: 2nd, 1st, 3rd
                        [1, 0, 2].map(rank => {
                            const row = rows[rank];
                            return (
                                <div key={rank} className={"leaderboard-podium-step rank" + rank}>
                                    {
                                        row ?
                                            <>
                                                <div className="leaderboard-podium-name"><FitText text={row.name}/></div>
                                                <div className="leaderboard-value">{board.value(row)}</div>
                                            </> :
                                            undefined
                                    }
                                    <div className="leaderboard-podium-block">
                                        <div className="leaderboard-rank">{rank + 1}</div>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
            </div>
            <div className="leaderboard-wide-right">
                {
                    rows.length === 0 ?
                        <div className="leaderboard-empty">Pas encore de partie</div> :
                        // no scroll here: three columns read top to bottom, what doesn't fit is cut
                        <div className="leaderboard-scroll">
                            <div className="leaderboard-scroll-content columns-3"
                                 style={{gridAutoFlow: "column", gridTemplateRows: "repeat(" + Math.max(1, Math.ceil((rows.length - FIXED_ROWS) / 3)) + ", auto)"}}>
                                {rows.slice(FIXED_ROWS).map((row, index) => renderRow(row, index, ""))}
                            </div>
                        </div>
                }
            </div>
        </div>
    );
}

// live leaderboard of the event, meant to be projected; refreshed every 5 minutes
export const LeaderboardAnniv2026Page = () => {

    const [player, setPlayer] = useState(undefined);
    const [data, setData] = useState(undefined);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!player || !player.admin) {
            return;
        }
        const load = () => post("/leaderboard").then(d => {
            setData(d);
            setError("");
        }).catch(err => setError(errorMessage(err)));
        load();
        const timer = setInterval(load, REFRESH);
        return () => clearInterval(timer);
    }, [player]);

    if (!player || !player.admin || !data) {
        return (
            <div className="anniv2026 anniv2026-reveal-login">
                {!player ? <Login2026 setPlayer={setPlayer}/> : undefined}
                {player && !player.admin ? <div>Page réservée à l'admin.</div> : undefined}
                {player && player.admin && !data ? <div>{error || "Chargement du classement..."}</div> : undefined}
            </div>
        );
    }

    return (
        <div className="anniv2026 anniv2026-reveal leaderboard">
            <div className="leaderboard-header">
                <img src="https://nostragames.ai/logo.png" alt="Nostra Games"/>
                <div className="leaderboard-title">
                    Concours 2026 · Classement
                    <div className="leaderboard-day">
                        Journée du {new Date(data.window.start).toLocaleDateString("fr-FR", {weekday: "long", day: "numeric", month: "long"})}
                    </div>
                </div>
                <div className="leaderboard-total">
                    <span>{data.totalParties}</span> parties jouées
                </div>
            </div>
            <div className="leaderboard-panels">
                <div className="leaderboard-panel players">
                    <div className="leaderboard-boards">
                        {
                            BOARDS.map(board =>
                                <PlayerBoard key={board.id} board={board} rows={data[board.id]}/>
                            )
                        }
                    </div>
                </div>
                <div className="leaderboard-panel games">
                    <Board board={GAMES_BOARD} rows={data.games}
                           renderContent={game =>
                               <>
                                   {game.logo ? <img className="leaderboard-game-logo" src={game.logo} alt=""/> : undefined}
                                   <div className="leaderboard-name"><FitText text={game.name}/></div>
                               </>
                           }/>
                </div>
            </div>
            {
                error || data.syncError ?
                    <div className="leaderboard-error">
                        {error || ("Synchro Nostra en échec : " + data.syncError.message)}
                    </div> :
                    undefined
            }
        </div>
    );
}
